import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Plus, FileText, ShieldCheck, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";

const ConfBar = ({ score }) => {
  const color = score >= 75 ? "bg-emerald-700" : score >= 50 ? "bg-amber-600" : "bg-red-600";
  return (
    <div className="w-full bg-slate-100 h-1.5">
      <div className={`${color} h-1.5`} style={{ width: `${score}%` }} />
    </div>
  );
};

export default function Suppliers() {
  const { user } = useAuth();
  const [suppliers, setSuppliers] = useState([]);
  const [evidence, setEvidence] = useState({});
  const [active, setActive] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [newEv, setNewEv] = useState({ doc_type: "iso_cert", file_name: "", description: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const r = await api.get("/suppliers");
    setSuppliers(r.data);
    if (r.data.length && !active) setActive(r.data[0]);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (active) {
      api.get(`/suppliers/${active.id}/evidence`).then((r) => setEvidence((p) => ({ ...p, [active.id]: r.data })));
    }
  }, [active]);

  const submitEvidence = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/evidence", { ...newEv, supplier_id: active.id });
      toast.success("Evidence submitted");
      setNewEv({ doc_type: "iso_cert", file_name: "", description: "" });
      const r = await api.get(`/suppliers/${active.id}/evidence`);
      setEvidence((p) => ({ ...p, [active.id]: r.data }));
    } catch { toast.error("Failed to add"); }
    finally { setBusy(false); }
  };

  const [newSup, setNewSup] = useState({
    name: "", country: "", materials: "", has_iso14001: false,
    renewable_energy_pct: 0, primary_data_pct: 0, third_party_verified: false, notes: "",
  });

  const addSupplier = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/suppliers", {
        ...newSup,
        materials: newSup.materials.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Supplier added");
      setShowNew(false);
      setNewSup({ name: "", country: "", materials: "", has_iso14001: false, renewable_energy_pct: 0, primary_data_pct: 0, third_party_verified: false, notes: "" });
      await load();
    } catch { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  const canAdd = ["admin","sustainability","procurement"].includes(user?.role);
  const list = evidence[active?.id] || [];

  return (
    <div data-testid="suppliers-page" className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-emerald-800">Supplier ESG & Emission Evidence Portal</div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight text-slate-900 mt-1">Suppliers</h1>
          <p className="text-sm text-slate-600 mt-2">Centralized portal for supplier ESG declarations, ISO certificates, energy mix and PCF reports.</p>
        </div>
        {canAdd && (
          <button data-testid="new-supplier-btn" onClick={() => setShowNew(!showNew)} className="inline-flex items-center gap-2 px-5 py-3 text-xs uppercase tracking-[0.2em] bg-emerald-800 text-white hover:bg-emerald-900">
            <Plus className="w-4 h-4" /> Onboard Supplier
          </button>
        )}
      </div>

      {showNew && (
        <form onSubmit={addSupplier} data-testid="new-supplier-form" className="border border-emerald-200 bg-emerald-50/40 p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[["name","Name"],["country","Country"],["materials","Materials (comma-sep)"]].map(([k,l]) => (
            <div key={k}>
              <label className="overline text-slate-500 block mb-1">{l}</label>
              <input data-testid={`new-supplier-${k}`} value={newSup[k]} onChange={(e) => setNewSup({ ...newSup, [k]: e.target.value })} required className="w-full px-3 py-2 border border-slate-300 text-sm font-mono" />
            </div>
          ))}
          {[["renewable_energy_pct","Renewable Energy %"],["primary_data_pct","Primary Data %"]].map(([k,l]) => (
            <div key={k}>
              <label className="overline text-slate-500 block mb-1">{l}</label>
              <input data-testid={`new-supplier-${k}`} type="number" min="0" max="100" value={newSup[k]} onChange={(e) => setNewSup({ ...newSup, [k]: Number(e.target.value) })} className="w-full px-3 py-2 border border-slate-300 text-sm font-mono" />
            </div>
          ))}
          <div className="flex items-center gap-4">
            <label className="text-sm flex items-center gap-2"><input data-testid="new-supplier-iso" type="checkbox" checked={newSup.has_iso14001} onChange={(e) => setNewSup({ ...newSup, has_iso14001: e.target.checked })} /> ISO 14001</label>
            <label className="text-sm flex items-center gap-2"><input data-testid="new-supplier-3pv" type="checkbox" checked={newSup.third_party_verified} onChange={(e) => setNewSup({ ...newSup, third_party_verified: e.target.checked })} /> 3rd-party verified</label>
          </div>
          <div className="md:col-span-3 flex justify-end gap-2">
            <button type="button" onClick={() => setShowNew(false)} className="px-4 py-2 text-xs uppercase tracking-[0.18em] border border-slate-300">Cancel</button>
            <button data-testid="submit-supplier" disabled={busy} className="px-5 py-2 text-xs uppercase tracking-[0.2em] bg-emerald-800 text-white inline-flex items-center gap-2">
              {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Onboard
            </button>
          </div>
        </form>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="space-y-2">
          {suppliers.map((s) => (
            <button
              key={s.id}
              data-testid={`supplier-${s.id}`}
              onClick={() => setActive(s)}
              className={`w-full text-left p-4 border transition-colors ${active?.id === s.id ? "border-emerald-700 bg-emerald-50" : "border-slate-200 bg-white hover:bg-slate-50"}`}
            >
              <div className="flex justify-between items-start">
                <div>
                  <div className="font-heading font-bold text-slate-900">{s.name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{s.country} · {s.materials.join(", ")}</div>
                </div>
                {s.has_iso14001 && <ShieldCheck className="w-4 h-4 text-emerald-700" />}
              </div>
              <div className="mt-3">
                <div className="flex justify-between text-[10px] uppercase tracking-[0.18em] text-slate-500 mb-1">
                  <span>Confidence</span><span className="font-mono">{s.confidence_score}/100</span>
                </div>
                <ConfBar score={s.confidence_score} />
              </div>
            </button>
          ))}
        </div>

        <div className="lg:col-span-2 border border-slate-200 p-6 bg-white">
          {active ? (
            <>
              <div className="overline text-emerald-800">Supplier Detail</div>
              <h2 className="font-heading text-2xl font-bold text-slate-900 mt-1">{active.name}</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                <div><div className="overline text-slate-500">Country</div><div className="font-mono mt-1">{active.country}</div></div>
                <div><div className="overline text-slate-500">Renewable</div><div className="font-mono mt-1">{active.renewable_energy_pct}%</div></div>
                <div><div className="overline text-slate-500">Primary Data</div><div className="font-mono mt-1">{active.primary_data_pct}%</div></div>
                <div><div className="overline text-slate-500">3rd-Party Verified</div><div className="font-mono mt-1">{active.third_party_verified ? "Yes" : "No"}</div></div>
              </div>
              {active.notes && <div className="mt-4 text-sm text-slate-600 italic">{active.notes}</div>}

              <div className="border-t border-slate-200 mt-6 pt-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="overline text-emerald-800">Evidence Documents</div>
                  <span className="text-xs text-slate-500">{list.length} on file</span>
                </div>

                <form onSubmit={submitEvidence} className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-4">
                  <select data-testid="ev-doc-type" value={newEv.doc_type} onChange={(e) => setNewEv({ ...newEv, doc_type: e.target.value })} className="px-3 py-2 border border-slate-300 text-sm bg-white">
                    <option value="iso_cert">ISO Certificate</option>
                    <option value="pcf_report">PCF Report</option>
                    <option value="energy_mix">Energy Mix</option>
                    <option value="transport">Transport Data</option>
                    <option value="recycling">Recycling Records</option>
                    <option value="msds">MSDS</option>
                  </select>
                  <input data-testid="ev-filename" placeholder="filename.pdf" value={newEv.file_name} onChange={(e) => setNewEv({ ...newEv, file_name: e.target.value })} required className="px-3 py-2 border border-slate-300 text-sm font-mono" />
                  <input data-testid="ev-description" placeholder="Description" value={newEv.description} onChange={(e) => setNewEv({ ...newEv, description: e.target.value })} className="px-3 py-2 border border-slate-300 text-sm" />
                  <button data-testid="submit-evidence" disabled={busy} className="px-4 py-2 bg-emerald-800 text-white text-xs uppercase tracking-[0.18em] inline-flex items-center gap-2 justify-center">
                    {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} <Plus className="w-3.5 h-3.5" /> Submit
                  </button>
                </form>

                <div className="space-y-2">
                  {list.map((e) => (
                    <div key={e.id} data-testid={`evidence-${e.id}`} className="border border-slate-200 p-3 flex items-start gap-3">
                      <FileText className="w-4 h-4 mt-1 text-slate-500" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="font-mono text-sm font-semibold text-slate-900">{e.file_name}</div>
                          <span className={`text-[10px] uppercase tracking-[0.18em] px-1.5 py-0.5 ${
                            e.status === "verified" ? "bg-emerald-100 text-emerald-900" :
                            e.status === "flagged" ? "bg-red-100 text-red-900" : "bg-slate-100 text-slate-700"
                          }`}>{e.status}</span>
                          <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500">{e.doc_type}</span>
                        </div>
                        <div className="text-xs text-slate-600 mt-1">{e.description}</div>
                        {e.ai_flag && (
                          <div className="text-xs text-red-700 mt-2 flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" /> {e.ai_flag}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {list.length === 0 && <div className="text-sm text-slate-500 italic">No evidence on file yet.</div>}
                </div>
              </div>
            </>
          ) : (
            <div className="text-slate-500">Select a supplier.</div>
          )}
        </div>
      </div>
    </div>
  );
}
